import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "../App";
import Watermark from '../components/Watermark';

const BOOKMAKER_LABELS = {
  betway_bw: 'Betway', pstbets_bw: 'PST Bets', sunbet_bw: 'Sunbet',
  bet267_bw: 'Bet267', bbets_bw: 'BBets', betxplosion_bw: 'BetXplosion',
};

const SPORT_EMOJI = { soccer: "⚽", basketball: "🏀", tennis: "🎾", americanfootball: "🏈", cricket: "🏏", rugby: "🏉" };
function sportEmoji(s = "") { return SPORT_EMOJI[Object.keys(SPORT_EMOJI).find(k => s.toLowerCase().includes(k))] || "🎯"; }

function formatKickoff(dt) {
  if (!dt) return "—";
  return new Date(dt).toLocaleDateString("en-BW", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function Arbitrage() {
  const [arbs, setArbs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [bankroll, setBankroll] = useState(100);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [filter, setFilter] = useState({ minProfit: 0 });
  const [calcStakes, setCalcStakes] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const isMobile = width < 768;

  const fetchArbs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 50 });
      if (filter.minProfit) params.set("minProfit", filter.minProfit);
      const data = await apiFetch(`/arbitrage?${params}`);
      setArbs(data.arbs || []);
      setLastRefresh(new Date());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchArbs(); }, [fetchArbs]);
  useEffect(() => {
  const t = setInterval(() => {
    if (!document.hidden) fetchArbs();
  }, 30000);
  
  const handleVisibility = () => {
    if (!document.hidden) fetchArbs();
  };
  document.addEventListener('visibilitychange', handleVisibility);
  
  return () => {
    clearInterval(t);
    document.removeEventListener('visibilitychange', handleVisibility);
  };
}, [fetchArbs]);

  const calculateForBankroll = async (arb) => {
    setCalcLoading(true);
    try {
      const data = await apiFetch(`/arbitrage/${arb._id}/calculate?bankroll=${bankroll}`);
      setCalcStakes(data);
    } catch (e) {
      // Calculate locally if API fails
      const legs = arb.legs || [];
      const bookPct = legs.reduce((s, l) => s + (1 / l.price), 0);
      const calculatedLegs = legs.map(l => ({
        selection: l.outcome,
        bookmakeName: BOOKMAKER_LABELS[l.bookmaker] || l.bookmaker,
        odds: l.price,
        stake: ((bankroll * (1 / l.price)) / bookPct).toFixed(2),
        payout: ((bankroll * (1 / l.price)) / bookPct * l.price).toFixed(2),
      }));
      const guaranteedReturn = (bankroll / bookPct).toFixed(2);
      setCalcStakes({
        legs: calculatedLegs,
        totalStake: bankroll,
        guaranteedReturn,
        profitBWP: (guaranteedReturn - bankroll).toFixed(2),
      });
    }
    finally { setCalcLoading(false); }
  };

  const handleSelect = (arb) => {
    setSelected(s => s?._id === arb._id ? null : arb);
    setCalcStakes(null);
  };

  // Summary stats
  const bestArb = arbs[0];
  const bestProfit = bestArb ? `+${bestArb.profitPct}%` : "—";
  const bestAt500 = bestArb ? `P${((bestArb.profitPct / 100) * 500).toFixed(0)}` : "—";

  return (
    <div className="no-select" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Watermark />

      {/* Summary stats — Fix: centered values on mobile */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)", gap: 10 }}>
        {[
          { label: "Active Arbs", value: loading ? "—" : arbs.length, sub: "↑ Scanned every 2 min", color: "var(--success)" },
          { label: "Best Profit", value: bestProfit, sub: "Guaranteed return", color: "var(--accent2)" },
          { label: "Best at P500", value: bestAt500, sub: "Guaranteed profit", color: "var(--accent3)" },
        ].map(s => (
          <div key={s.label} className="stat-card" style={{ "--accent-color": s.color, textAlign: "center" }}>
            <div className="stat-label" style={{ textAlign: "center" }}>{s.label}</div>
            <div className="stat-value" style={{ textAlign: "center", fontSize: isMobile ? 18 : 28, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.value}</div>
            <div className="stat-change up" style={{ textAlign: "center", justifyContent: "center", fontSize: 10, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Explainer */}
      <div style={{
        background: "rgba(61,142,248,0.06)", border: "1px solid rgba(61,142,248,0.15)",
        borderRadius: "var(--radius)", padding: "14px 18px",
        display: "flex", gap: 14, alignItems: "flex-start",
      }}>
        <div style={{ fontSize: 22, flexShrink: 0 }}>💡</div>
        <div>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 13, marginBottom: 4 }}>How arbitrage works</div>
          <div style={{ fontSize: 12, color: "var(--text3)", lineHeight: 1.6 }}>
            When different BW bookmakers price the same match differently, the combined implied probability can drop below 100% — creating a guaranteed profit regardless of the outcome. Place the exact stake on each leg to lock in the return.
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ padding: "12px 18px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <label style={{ fontSize: 11, color: "var(--text3)", fontFamily: "var(--font-display)", textTransform: "uppercase", letterSpacing: .5, whiteSpace: "nowrap" }}>Min Profit %</label>
            <select value={filter.minProfit} onChange={e => setFilter(f => ({ ...f, minProfit: parseFloat(e.target.value) }))} style={{ fontSize: 13 }}>
              <option value={0}>Any</option>
              <option value={0.5}>+0.5%</option>
              <option value={1}>+1%</option>
              <option value={2}>+2%</option>
              <option value={3}>+3%</option>
            </select>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
            {lastRefresh && <span style={{ fontSize: 11, color: "var(--text3)" }}>Updated {lastRefresh.toLocaleTimeString()}</span>}
            <button className="btn btn-outline" style={{ fontSize: 11, padding: "5px 12px" }} onClick={fetchArbs} disabled={loading}>
              ↺ Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Arbs list + detail */}
      <div style={{ display: "grid", gridTemplateColumns: selected && !isMobile ? "1fr 380px" : "1fr", gap: 16, alignItems: "start" }}>
        <div className="card">
          {loading ? (
            <div style={{ padding: 20 }}>
              {[...Array(5)].map((_, i) => <div key={i} className="skeleton" style={{ height: 80, marginBottom: 10 }} />)}
            </div>
          ) : arbs.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">⚡</div>
              <p>No arbitrage opportunities right now.</p>
              <p style={{ marginTop: 6, fontSize: 12 }}>The scanner checks every 2 minutes — check back soon.</p>
            </div>
          ) : arbs.map(arb => (
            <div
              key={arb._id}
              onClick={() => handleSelect(arb)}
              style={{
                padding: isMobile ? "12px 14px" : "16px 20px",
                borderBottom: "1px solid var(--border)",
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr auto" : "36px 1fr auto",
                gap: isMobile ? 10 : 14,
                alignItems: "center",
                cursor: "pointer",
                background: selected?._id === arb._id ? "var(--bg4)" : "transparent",
                transition: "background .12s",
              }}
            >
              {!isMobile && (
                <div className="sport-badge" style={{ background: "rgba(0,229,176,0.07)", fontSize: 16 }}>{sportEmoji(arb.sport)}</div>
              )}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: isMobile ? 13 : 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {isMobile ? sportEmoji(arb.sport) + " " : ""}{arb.homeTeam} vs {arb.awayTeam}
                </div>
                <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>
                  {arb.market?.toUpperCase()} · {arb.legs?.length || 2} legs
                </div>
                <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {arb.legs?.map((leg, i) => (
                    <span key={i} className="pill pill-blue" style={{ fontSize: 10 }}>
                      {leg.outcome} @ {leg.price} — {BOOKMAKER_LABELS[leg.bookmaker] || leg.bookmaker}
                    </span>
                  ))}
                </div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: isMobile ? 18 : 22, color: "var(--success)" }}>
                  +{arb.profitPct}%
                </div>
                <div style={{ fontSize: 11, color: "var(--text3)" }}>guaranteed</div>
                <div style={{ fontSize: 12, color: "var(--accent)", marginTop: 4, fontWeight: 600 }}>
                  P{arb.profitPct?.toFixed(2)} / P100
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Detail + calculator — modal on mobile */}
        {selected && (
          isMobile ? (
            <div style={{
              position: 'fixed', inset: 0, zIndex: 200,
              background: 'rgba(8,13,23,0.97)',
              backdropFilter: 'blur(8px)',
              overflow: 'auto', padding: '20px 16px',
            }}>
              <div className="card" style={{ maxWidth: 480, margin: '0 auto' }}>
                <div className="section-header">
                  <div className="section-title">⚡ Arb Calculator</div>
                  <button onClick={() => { setSelected(null); setCalcStakes(null); }} style={{ color: "var(--text3)", fontSize: 22, background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
                </div>
                <ArbDetail selected={selected} bankroll={bankroll} setBankroll={setBankroll} calcStakes={calcStakes} calcLoading={calcLoading} onCalculate={() => calculateForBankroll(selected)} />
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 14, position: "sticky", top: 80 }}>
              <div className="card">
                <div className="section-header">
                  <div className="section-title">⚡ Arb Calculator</div>
                  <button onClick={() => { setSelected(null); setCalcStakes(null); }} style={{ color: "var(--text3)", fontSize: 18, background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
                </div>
                <ArbDetail selected={selected} bankroll={bankroll} setBankroll={setBankroll} calcStakes={calcStakes} calcLoading={calcLoading} onCalculate={() => calculateForBankroll(selected)} />
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
}

function ArbDetail({ selected, bankroll, setBankroll, calcStakes, calcLoading, onCalculate }) {
  return (
    <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 15 }}>
          {selected.homeTeam} vs {selected.awayTeam}
        </div>
        <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 3 }}>
          {selected.sport} · {formatKickoff(selected.commenceTime)}
        </div>
      </div>

      <div style={{
        background: "rgba(18,201,142,0.06)", border: "1px solid rgba(18,201,142,0.2)",
        borderRadius: "var(--radius-sm)", padding: 14, textAlign: "center"
      }}>
        <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 32, color: "var(--success)" }}>
          +{selected.profitPct}%
        </div>
        <div style={{ fontSize: 12, color: "var(--text3)" }}>guaranteed profit margin</div>
      </div>

      <div>
        <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 6, fontFamily: "var(--font-display)", textTransform: "uppercase", letterSpacing: .5 }}>
          Total stake (BWP)
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            type="number" value={bankroll}
            onChange={e => setBankroll(parseFloat(e.target.value) || 100)}
            style={{ flex: 1, fontSize: 16, fontFamily: "var(--font-display)", fontWeight: 700 }}
            placeholder="100"
          />
          <button className="btn btn-primary" onClick={onCalculate} disabled={calcLoading}>
            {calcLoading ? "..." : "Calculate"}
          </button>
        </div>
      </div>

      {calcStakes && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 11, color: "var(--text3)", fontFamily: "var(--font-display)", textTransform: "uppercase", letterSpacing: .5 }}>Place these bets:</div>
          {calcStakes.legs?.map((leg, i) => (
            <div key={i} style={{ background: "var(--bg3)", borderRadius: "var(--radius-sm)", padding: "12px 14px", display: "grid", gridTemplateColumns: "1fr auto" }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{leg.selection}</div>
                <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>
                  {BOOKMAKER_LABELS[leg.bookmaker] || leg.bookmaker} · @ {leg.odds}
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, color: "var(--accent)", fontSize: 16 }}>P{leg.stake}</div>
                <div style={{ fontSize: 10, color: "var(--text3)" }}>→ P{leg.payout}</div>
              </div>
            </div>
          ))}
          <div style={{
            background: "rgba(18,201,142,0.08)", border: "1px solid rgba(18,201,142,0.2)",
            borderRadius: "var(--radius-sm)", padding: "12px 14px",
            display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12,
          }}>
            <div>
              <div style={{ fontSize: 11, color: "var(--text3)" }}>Total staked</div>
              <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 16 }}>P{calcStakes.totalStake}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 11, color: "var(--text3)" }}>Guaranteed return</div>
              <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 16, color: "var(--success)" }}>P{calcStakes.guaranteedReturn}</div>
            </div>
            <div style={{ gridColumn: "1/-1", borderTop: "1px solid rgba(18,201,142,0.2)", paddingTop: 10, textAlign: "center" }}>
              <span style={{ fontSize: 12, color: "var(--text3)" }}>Guaranteed profit: </span>
              <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 18, color: "var(--success)" }}>+P{calcStakes.profitBWP}</span>
            </div>
          </div>
          <div style={{ fontSize: 11, color: "var(--text3)", textAlign: "center", lineHeight: 1.5 }}>
            ⚠️ Place all bets simultaneously — arb windows close fast
          </div>
        </div>
      )}
    </div>
  );
}